import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import Navigation from "./Navigation";
import Card from "./Card";
import Button from "./Buttons";
import TPRMExistingRisksDialog from "./TPRMExistingRisksDialog";
import { Upload, Loader, AlertCircle, Clock, FileText, X } from "lucide-react";

const ALLOWED_EXTENSIONS = [".pdf", ".docx", ".txt", ".csv"];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const TPRMDocumentAssessment = () => {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [step, setStep] = useState("upload"); // upload | scanning | analyzing
  const [error, setError] = useState(null);

  const [assessmentId, setAssessmentId] = useState(null);
  const [merged, setMerged] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisError, setAnalysisError] = useState(null);
  const [isAnalysisTimeout, setIsAnalysisTimeout] = useState(false);
  const [showExistingRisksDialog, setShowExistingRisksDialog] = useState(false);
  const [existingRisksInfo, setExistingRisksInfo] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const apiCall = useApi();
  const { canManageOrganizationRisks } = useRBAC();
  const fileInputRef = useRef(null);

  // Expect vendorId, dealId, businessUnitId passed via navigation state
  const { vendorId, vendorName, dealId, businessUnitId } = location.state || {};

  useEffect(() => {
    if (!canManageOrganizationRisks()) {
      navigate("/TPRMDashboard", { replace: true });
    }
    if (!vendorId) {
      navigate("/TPRMApplications", { replace: true });
    }
  }, [canManageOrganizationRisks, navigate, vendorId]);

  const validateFile = useCallback((f) => {
    if (!f) return "Please select a file.";
    if (f.size > MAX_FILE_SIZE) return "File size must not exceed 10 MB.";
    const ext = f.name.substring(f.name.lastIndexOf(".")).toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) return "Only PDF, DOCX, TXT, and CSV files are supported.";
    return null;
  }, []);

  const handleFileSelect = useCallback((f) => {
    const validationError = validateFile(f);
    if (validationError) {
      setError(validationError);
      setFile(null);
      return;
    }
    setError(null);
    setFile(f);
  }, [validateFile]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) handleFileSelect(droppedFile);
  }, [handleFileSelect]);

  const handleSubmit = async () => {
    if (!file) return;

    setError(null);

    // Pre-check for existing risks
    try {
      const params = new URLSearchParams({ vendorId });
      if (dealId) params.append("dealId", dealId);
      if (businessUnitId) params.append("businessUnitId", businessUnitId);

      const checkResult = await apiCall(
        "GET",
        `/tprmAssessments/existing-risks-check?${params.toString()}`
      );

      if (checkResult.hasExistingRisks) {
        setExistingRisksInfo(checkResult);
        setShowExistingRisksDialog(true);
        return;
      }
    } catch (err) {}

    await uploadDocument(null);
  };

  const handleExistingRisksConfirm = async (strategy) => {
    setShowExistingRisksDialog(false);
    await uploadDocument(strategy);
  };

  const uploadDocument = async (existingRiskStrategy) => {
    setStep("scanning");
    setError(null);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("vendorId", vendorId);
    if (dealId) formData.append("dealId", dealId);
    if (businessUnitId) formData.append("businessUnitId", businessUnitId);
    if (existingRiskStrategy) formData.append("existingRiskStrategy", existingRiskStrategy);

    try {
      const result = await apiCall("POST", "/tprmAssessments/document-assessment", formData, true);
      setAssessmentId(result.assessmentId);
      setMerged(result.merged ?? false);
      setStep("analyzing");
      setAnalysisProgress(0);
      setAnalysisError(null);
      setIsAnalysisTimeout(false);
    } catch (err) {
      const message = err.response?.data?.message || "An error occurred. Please try again.";
      setError(message);
      setStep("upload");
    }
  };

  // Poll for analysis completion
  useEffect(() => {
    if (step !== "analyzing" || !assessmentId) return;

    let pollCount = 0;
    const maxPolls = 60;

    const pollStatus = async () => {
      try {
        const statusResponse = await apiCall(
          "GET",
          `/tprmAssessments/${assessmentId}/analysis-status`
        );

        setAnalysisProgress(Math.round(statusResponse.analysisProgress) || 0);

        if (statusResponse.analysisProgress >= 100) {
          setAnalysisProgress(100);
          navigate(`/VendorDashboard/${vendorId}`);
          return;
        }

        if (statusResponse.status?.toLowerCase() === "analysisfailed") {
          setAnalysisError("Analysis failed. Please try again or contact support.");
          return;
        }

        pollCount++;
        if (pollCount >= maxPolls) {
          setIsAnalysisTimeout(true);
          setAnalysisError(
            "Analysis is still in progress. In some cases, this can take 10 minutes or longer. You can safely leave this page — your results will be available when analysis completes."
          );
        }
      } catch (err) {
        pollCount++;
        if (pollCount >= maxPolls) {
          setIsAnalysisTimeout(true);
          setAnalysisError(
            "Analysis is still in progress. In some cases, this can take 10 minutes or longer. You can safely leave this page — your results will be available when analysis completes."
          );
        }
      }
    };

    pollStatus();
    const intervalId = setInterval(pollStatus, 5000);
    return () => clearInterval(intervalId);
  }, [step, assessmentId, apiCall, navigate]);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (step === "scanning") {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-2xl mx-auto py-8 px-4">
          <Card>
            <div className="text-center py-12">
              <Loader className="h-16 w-16 text-blue-600 mx-auto mb-6 animate-spin" />
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Scanning Document for Malware</h2>
              <p className="text-gray-600 mb-2">Your document is being scanned for security threats...</p>
              <p className="text-sm text-gray-400">This may take up to a minute</p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (step === "analyzing") {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-2xl mx-auto py-8 px-4">
          <Card>
            <div className="text-center py-12">
              {analysisError ? (
                <>
                  {isAnalysisTimeout ? (
                    <Clock className="h-16 w-16 text-amber-500 mx-auto mb-6" />
                  ) : (
                    <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-6" />
                  )}
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    {isAnalysisTimeout ? "Analysis Taking Longer Than Expected" : "Analysis Failed"}
                  </h2>
                  <p className="text-gray-600 mb-8">{analysisError}</p>
                  <div className="flex justify-center gap-4">
                    <Button variant="secondary" onClick={() => navigate("/TPRMDashboard")}>
                      View Dashboard
                    </Button>
                    {assessmentId && (
                      <Button variant="primary" onClick={() => navigate(`/VendorDashboard/${vendorId}`)}>
                        Check Results
                      </Button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <Loader className="h-16 w-16 text-blue-600 mx-auto mb-6 animate-spin" />
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Analyzing Document</h2>
                  {merged && (
                    <p className="text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded px-3 py-2 mb-4 inline-block">
                      Adding document-derived risks to your existing assessment
                    </p>
                  )}
                  <p className="text-gray-600 mb-2">
                    Our AI is analyzing your document and generating risk insights.
                  </p>
                  <p className="text-gray-600 mb-6">
                    This typically takes a few minutes, but in some cases may take 10 minutes or longer.
                  </p>
                  <div className="max-w-md mx-auto mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-500">Progress</span>
                      <span className="text-sm font-medium text-gray-700">{analysisProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-blue-600 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${analysisProgress}%` }}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-2xl mx-auto py-8 px-4">
        <Card>
          <div className="p-2">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">TPRM Document Assessment</h2>
            {vendorName && (
              <p className="text-sm text-gray-500 mb-1">Vendor: <span className="font-medium text-gray-700">{vendorName}</span></p>
            )}
            <p className="text-gray-600 mb-6">
              Upload a vendor document (e.g., SOC 2 report, security questionnaire, policy document). Our AI will
              analyze it and add the findings to your TPRM assessment.
              {!dealId && !businessUnitId && (
                <span className="block mt-1 text-sm text-blue-600">
                  If a Q&A assessment already exists for this vendor, document risks will be added to it automatically.
                </span>
              )}
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
                <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                dragOver
                  ? "border-blue-500 bg-blue-50"
                  : file
                  ? "border-green-300 bg-green-50"
                  : "border-gray-300 hover:border-gray-400"
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.txt,.csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFileSelect(f);
                }}
              />

              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="h-8 w-8 text-green-600" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900">{file.name}</p>
                    <p className="text-sm text-gray-500">{formatFileSize(file.size)}</p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    className="ml-2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 mb-1">Drag and drop a file here, or click to browse</p>
                  <p className="text-sm text-gray-400">PDF, DOCX, TXT, or CSV (max 10 MB)</p>
                </>
              )}
            </div>

            <div className="mt-6 flex justify-between">
              <Button variant="secondary" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button variant="primary" disabled={!file} onClick={handleSubmit}>
                Submit Assessment
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <TPRMExistingRisksDialog
        isOpen={showExistingRisksDialog}
        onClose={() => setShowExistingRisksDialog(false)}
        onConfirm={handleExistingRisksConfirm}
        existingRisksInfo={existingRisksInfo}
      />
    </div>
  );
};

export default TPRMDocumentAssessment;
